import { useCallback, useEffect, useRef } from 'react';
import { Marker, useMapEvents } from 'react-leaflet';
import L from './leafletDrawAreaFix';
import { isLeafletChromeTarget, useIsDrawingMode } from './mapEventGuards';

/**
 * Pre-built default marker icon. The Leaflet defaults rely on CSS image
 * paths that webpack/vite mangle — pointing at unpkg keeps the icon visible
 * regardless of the bundler's asset handling.
 */
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface LocationMarkerProps {
  /** Current pin position, or null when nothing is placed yet. */
  position: [number, number] | null;
  /** Called with the new [lat, lng] when the user clicks the map. */
  onChange: (next: [number, number]) => void;
  /**
   * When false, the click handler is inert — useful when the parent wants to
   * keep the marker visible but stop new clicks from moving it.
   */
  enabled?: boolean;
}

/**
 * Click-to-place marker. Watches the map for `draw:*` events from
 * react-leaflet-draw and disables itself while the user is actively drawing
 * or editing a polygon, so clicks meant for the draw toolbar don't accidentally
 * relocate the pin.
 *
 * Originally embedded inside `MapComponent.jsx`; extracted so the unified
 * `MapCanvas` can opt into the same behaviour without pulling the rest of
 * that monolith.
 */
export function LocationMarker({
  position,
  onChange,
  enabled = true,
}: LocationMarkerProps) {
  const mapRef = useRef<L.Map | null>(null);
  const isDrawingMode = useIsDrawingMode();

  const shouldHandleClicks = useCallback(
    () => enabled && !isDrawingMode,
    [enabled, isDrawingMode],
  );

  const map = useMapEvents({
    click: (e) => {
      if (!shouldHandleClicks()) return;

      // Ignore clicks that landed on an existing Leaflet element (draw
      // toolbar buttons, other markers, drawn shapes, popups). Without this
      // the pin can jump while the user is aiming at the toolbar.
      if (isLeafletChromeTarget(e.originalEvent?.target)) return;

      if (e.latlng && typeof e.latlng.lat === 'number' && typeof e.latlng.lng === 'number') {
        onChange([e.latlng.lat, e.latlng.lng]);
        // Center on the new pin so it doesn't slide off-screen on small displays.
        mapRef.current?.flyTo(e.latlng, mapRef.current.getZoom());
      }
    },
  });

  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  return (
    <>
      {position && <Marker position={position} icon={defaultIcon} />}
      {isDrawingMode && enabled && (
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            backgroundColor: 'rgba(255, 152, 0, 0.95)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 20,
            pointerEvents: 'none',
            fontSize: 12,
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            border: '2px solid #FF9800',
          }}
        >
          ✏️ Drawing mode — location selection disabled
        </div>
      )}
    </>
  );
}

export default LocationMarker;
